import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useNavBarTitle } from "components/NavBar";
import ProjectPicker from "./ProjectPicker";
import StatusSummary from "./StatusSummary";
import StatusTable from "./StatusTable";
import UpdatedAt from "./UpdatedAt";
import t from "./translations";
import useLidarUploadStatus from "./useLidarUploadStatus";
import useSelectedProjects from "./useSelectedProjects";

export default function LidarUploadStatus() {
  useNavBarTitle(t.PageTitle);
  const { selectedProjectReferences, setSelectedProjectReferences } =
    useSelectedProjects();
  const { projectStatuses, loading, error, lastLoadedAt, refresh } =
    useLidarUploadStatus(selectedProjectReferences);
  const hasSelection = selectedProjectReferences.length > 0;
  const hasProjectStatuses = projectStatuses.length > 0;

  const addProject = (addedReference: string) =>
    setSelectedProjectReferences((currentReferences) => [
      ...currentReferences,
      addedReference,
    ]);

  const removeProject = (removedReference: string) =>
    setSelectedProjectReferences((currentReferences) =>
      currentReferences.filter((reference) => reference !== removedReference)
    );

  return (
    <Box
      sx={{ p: 2, display: "grid", gap: 2 }}
      data-testid="lidar-upload-status-page"
    >
      <Box
        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}
      >
        {hasProjectStatuses && (
          <StatusSummary projectStatuses={projectStatuses} />
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          {hasSelection && lastLoadedAt && (
            <UpdatedAt lastLoadedAt={lastLoadedAt} />
          )}
          <Tooltip title={t.Refresh} arrow>
            <span>
              <IconButton
                aria-label={t.Refresh}
                disabled={loading || !hasSelection}
                onClick={refresh}
                data-testid="lidar-upload-status.refresh"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <ProjectPicker
            selectedProjectReferences={selectedProjectReferences}
            onAdd={addProject}
          />
        </Box>
      </Box>

      {!hasSelection && (
        <Typography color="text.secondary">{t.NoProjectsSelected}</Typography>
      )}
      {error && !hasProjectStatuses && (
        <Typography color="error">{t.LoadFailed}</Typography>
      )}
      {loading && !hasProjectStatuses && (
        <Typography color="text.secondary">{t.Loading}</Typography>
      )}
      {hasProjectStatuses && (
        <StatusTable
          projectStatuses={projectStatuses}
          onRemoveProject={removeProject}
        />
      )}
    </Box>
  );
}
